import {MyopWebComponent} from "../components/myopWebcomponent";
import {MyopExtractedWebComponent} from "../components/myopExtractedWebComponent";
import {BaseMyopLoader} from "./IMyopLoader";
import {loaderOptions} from "../hostSDK.ts";
import {uuidv4} from "../../utils/utils.ts";
import {
    IComponentDefinitionConfig,
    IHTMLLoaderConfig,
    ISkinConfig, ShadowRootModeType,
    SkinLoaders
} from "../../common";
import {WebComponentSDK} from "../../webcomponent";
import {MyopIframeComponent} from "../components/myopIframeComponent.ts";
import {EMBEDDED_SDK_CONTENT, HAS_EMBEDDED_SDK} from "../embeddedSDK.ts";
//import {getWebcomponentModule} from "../../SDK.ts";

const MYOP_WEB_COMPONENT_MARKER = '<!-- myop-web-component -->';
const MYOP_WEB_COMPONENT_SCRIPT_ID = 'myop-web-component';

// Track extracted web component scripts to prevent double registration
const extractedWebComponentScripts: Set<string> = new Set();

const seen: string[] = [];
let sdk;
const getInitiatedSdk = async () => {
    if (!sdk) {
        //const {WebComponentSDK} = await getWebcomponentModule()
        sdk = new WebComponentSDK();
        sdk.init();
    }
    return sdk;
}

function createHTMLElementConstructor(htmlString: string, ShadowRootMode?: keyof typeof ShadowRootModeType): new () => HTMLElement {
    return class extends HTMLElement {

        connectedCallback() {
            const mode = ShadowRootMode === ShadowRootModeType.open ? 'open' : 'closed';
            const shadow = this.attachShadow({mode});
            getInitiatedSdk().then((sdk) => {
                sdk.connectedCallback(this, shadow);
                shadow.innerHTML = htmlString
            })
        }
    };
}

const createCustomWebComponent = (componentName: string, html: string, ShadowRootMode?: keyof typeof ShadowRootModeType) => {
    customElements.define(componentName, createHTMLElementConstructor(html, ShadowRootMode));
    console.log(`define('${componentName}') was called, web component ready to use`);
}

const toWebComponentName = (str) => {
    return str
        .replace(/([a-z])([A-Z])/g, '$1-$2') // Add hyphen before uppercase letters
        .replace(/(\d+)/g, '-$1') // Add hyphen before digits
        .toLowerCase(); // Convert to lowercase
}

const toCustomElementName = (name: string) => {
    name = toWebComponentName(name);
    return name.replace(/_/g, '-');
}

let parser: DOMParser; // = new DOMParser();

export class HTMLComponentLoader extends BaseMyopLoader {

    type = SkinLoaders.HTMLLoader

    /**
     * Detect if HTML is a Myop Web Component (starts with marker comment)
     */
    private isMyopWebComponent(html: string): boolean {
        return html.trimStart().startsWith(MYOP_WEB_COMPONENT_MARKER);
    }

    /**
     * Extract the web component script content from HTML
     */
    private extractWebComponentScript(html: string): string | null {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const script = doc.getElementById(MYOP_WEB_COMPONENT_SCRIPT_ID);
        return script?.textContent || null;
    }

    /**
     * Execute the web component script on the host document
     */
    private executeWebComponentScript(scriptContent: string, scriptId: string): void {
        // Check if already executed (by script content hash)
        if (extractedWebComponentScripts.has(scriptId)) {
            console.log(`[MyopWebComponent] Script already executed for ${scriptId}, skipping`);
            return;
        }

        const script = document.createElement('script');
        script.textContent = scriptContent;
        script.id = `myop-extracted-${scriptId}`;
        document.head.appendChild(script);

        extractedWebComponentScripts.add(scriptId);
        console.log(`[MyopWebComponent] Executed web component script: ${scriptId}`);
    }

    /**
     * Load a Myop Web Component (extracted and run on host document)
     */
    private async loadExtractedWebComponent(
        componentDefinition: IComponentDefinitionConfig,
        skin: ISkinConfig,
        container: HTMLElement,
        html: string,
        options?: loaderOptions
    ): Promise<MyopExtractedWebComponent> {
        const scriptContent = this.extractWebComponentScript(html);

        if (!scriptContent) {
            throw new Error('[MyopWebComponent] No <script id="myop-web-component"> found in HTML');
        }

        // Use component definition id as script identifier for deduplication
        const scriptId = `${componentDefinition.id}-${skin.id}`;

        // Store __MYOP_TAG_NAME__ before execution to capture it
        const previousTagName = (window as any).__MYOP_TAG_NAME__;

        // Execute the script on the host document
        this.executeWebComponentScript(scriptContent, scriptId);

        // Get the tag name that was set by the script
        const tagName = (window as any).__MYOP_TAG_NAME__;

        if (!tagName) {
            throw new Error('[MyopWebComponent] Script did not set window.__MYOP_TAG_NAME__');
        }

        // Restore previous tag name if this was a re-registration
        if (previousTagName && previousTagName !== tagName) {
            console.warn(`[MyopWebComponent] __MYOP_TAG_NAME__ changed from ${previousTagName} to ${tagName}`);
        }

        // Create the custom element
        const domId = `myop-comp-${uuidv4()}`;
        const element = document.createElement(tagName);
        element.id = domId;

        if (options?.hidden) {
            element.style.opacity = '0';
            element.style.position = 'absolute';
            element.style.height = '0';
            element.style.width = '0';
            element.style.pointerEvents = 'none';
            element.style.visibility = 'hidden';
        }

        // Clear container if needed
        if (!container.querySelector('[id^="myop-comp-"]')) {
            container.innerHTML = '';
        }

        this.appendChild(container, element, options);

        // Create the component wrapper
        const component = new MyopExtractedWebComponent(
            componentDefinition,
            skin,
            container,
            element,
            tagName,
            options
        );

        // Initialize with data if provided
        if (options?.data !== undefined) {
            component.initWithData(options.data);
        }

        console.log(`[MyopWebComponent] Loaded <${tagName}> into container`);

        return component;
    }

    load = async (componentDefinition: IComponentDefinitionConfig, skin: ISkinConfig, container: HTMLElement, options?: loaderOptions) => {

        const loaderConfig = (skin.loader as IHTMLLoaderConfig);

        // Check if this is a Myop Web Component (extracted web component mode)
        if (this.isMyopWebComponent(loaderConfig.HTML)) {
            return this.loadExtractedWebComponent(componentDefinition, skin, container, loaderConfig.HTML, options);
        }

        let domId = `myop-comp-${uuidv4()}`
        let element;

        if (loaderConfig.shadowRootMode === ShadowRootModeType.localFrame) {
            const uniqId = uuidv4();
            const domId = `myop-comp-${uniqId}`

            let IframeElement = document.createElement('iframe');
            IframeElement.id = domId;

            // Set all styles BEFORE appending to prevent flash at wrong position
            IframeElement.style.cssText = `
                display: block;
                padding: 0;
                margin: 0;
                position: absolute;
                inset: 0;
                overflow: hidden;
                border: none;
                opacity: ${options?.hidden ? '0' : '1'};
                width: 100%;
                height: 100%;
                pointer-events: ${options?.hidden ? 'none' : 'all'};
            `;

            // TODO : do it only with flag ?
            if (!container.querySelector('[id^="myop-comp-"]'))
                container.innerHTML = '';

            IframeElement = this.appendChild(container, IframeElement, options) as HTMLIFrameElement;

            // Write content into the iframe
            //@ts-ignore
            const doc = IframeElement.contentDocument || IframeElement.contentWindow.document;
            doc.open();


            // Parse myop:size meta tag before iframe creation (same DOMParser pass pattern as myop_preview removal).
            // This lets the React host determine sizing mode (fill vs content) from the start.
            let parsedSizeMeta: { width?: string | number; height?: string | number; minWidth?: number; maxWidth?: number; minHeight?: number; maxHeight?: number } | null = null;
            {
                const _preParser = new DOMParser();
                const _preDoc = _preParser.parseFromString(loaderConfig.HTML, 'text/html');
                const sizeMeta = _preDoc.querySelector('meta[name="myop:size"]');
                if (sizeMeta) {
                    try { parsedSizeMeta = JSON.parse(sizeMeta.getAttribute('content') || '{}'); } catch {}
                }
            }

            let HTML2Render = loaderConfig.HTML;

            if (options?._inspection) {
                //inject inspection script for props detection
                const parser = new DOMParser();
                const _doc = parser.parseFromString(loaderConfig.HTML, 'text/html');

                // TODO : part of AI components, think about that, document it.
                const myop_preview_script = _doc.getElementById('myop_preview');
                myop_preview_script?.removeAttribute('type');

                // Parse the injected HTML string into a node
                const temp = _doc.implementation.createHTMLDocument();
                temp.body.innerHTML = `<script>
                        const beforeKeysWindow = new Set(Object.keys(window));
                        window.myopState = {
                            beforeKeysWindow,
                        };
                </script>`;

                const node = temp.body.firstChild;

                if (node && _doc.head) {
                    _doc.head.insertBefore(_doc.importNode(node, true), _doc.head.firstChild);
                }

                // Prepend DOCTYPE: documentElement.outerHTML returns only <html>...</html>
                // and drops the doctype, which would put the iframe in quirks mode.
                // Quirks mode breaks auto-sizing because <body> stretches to the iframe
                // viewport instead of shrinking to content, so ResizeObserver on body
                // never fires when content reflows (e.g. after font swap).
                HTML2Render = '<!DOCTYPE html>' + _doc.documentElement.outerHTML;
            } else if (options?.data !== undefined) {

                //TODO: improve by do it on the serve once

                // Remove myop_preview script when not in inspection mode
                const parser = new DOMParser();
                const _doc = parser.parseFromString(loaderConfig.HTML, 'text/html');
                const myop_preview_script = _doc.getElementById('myop_preview');

                if (myop_preview_script) {
                    myop_preview_script.remove();
                    // See note above: prepend DOCTYPE to keep the iframe in standards mode.
                    HTML2Render = '<!DOCTYPE html>' + _doc.documentElement.outerHTML;
                }
            }

            // Inject host-provided CSS before the iframe is written so styles are
            // present on first paint (no FOUC). Appended last in <head> so it wins
            // over the component's own styles via normal cascade order.
            if (options?.injectCss) {
                const parser = new DOMParser();
                const _doc = parser.parseFromString(HTML2Render, 'text/html');
                const styleTag = _doc.createElement('style');
                styleTag.setAttribute('data-myop-injected', 'true');
                styleTag.textContent = options.injectCss;
                (_doc.head || _doc.documentElement).appendChild(styleTag);
                // See note above: prepend DOCTYPE to keep the iframe in standards mode.
                HTML2Render = '<!DOCTYPE html>' + _doc.documentElement.outerHTML;
            }


            doc.writeln(HTML2Render);

            // window.__federation__ =
            //     { ...window.__federation__,
            //         //TODO : get from ENV at build time,
            //         // inject value to module at build time
            //         // today we doing so just for the CJS version
            //         __public_path__ :'https://cdn.myop.dev/sdk/next'
            //         ,...${JSON.stringify(
            //                 // @ts-ignore
            //                 window.__federation__)
            //              }
            //     };

            // For npm package users with bundlers (Vite/Webpack), inject SDK inline
            // For CDN users (script tag), use CDN URL
            const sdkScript = HAS_EMBEDDED_SDK
                ? `<script>${EMBEDDED_SDK_CONTENT}</script>`
                : `<script src="${window.myop.__ROOT_SDK_PUBLIC_URL__}"></script>`;

            // Different init script for embedded vs CDN SDK
            // - Embedded (bundled IIFE): uses MyopSDK.getIframeModule() which returns module directly
            // - CDN (dynamic loading): uses window.myop.rootSDK.getIframeModule() which wraps with .get()
            const initScript = HAS_EMBEDDED_SDK
                ? `<script>
                   const __myop_init = async () => {
                        const iframeModule = await MyopSDK.getIframeModule();
                        const IframeSDK = iframeModule.IframeSDK;
                        const sdk = new IframeSDK();
                        sdk.init();
                   }
                   __myop_init().then();
                </script>`
                : `<script>
                   window.__federation__.__public_path__ = window.__federation__.__public_path__;
                   const __myop_init = async () => {
                        const {IframeSDK} = (await window.myop.rootSDK.getIframeModule());
                        const sdk = new IframeSDK();
                        sdk.init();
                   }
                   __myop_init().then();
                </script>`;

            doc.writeln(`${sdkScript}${initScript}`);

            // Inject data script if data option is provided
            if (options?.data !== undefined) {
                // Setup CTA queue - captures any CTAs emitted during init before host registers handler
                const ctaQueueScript = `<script>
                    window.__myop_cta_queue = [];
                    window.myop_cta_handler = function(action, payload) {
                        window.__myop_cta_queue.push({action: action, payload: payload});
                    };
                </script>`;

                // Escape data for safe embedding in script tag
                // JSON.stringify doesn't escape </script> which could break HTML parsing
                const escapedData = JSON.stringify(options.data)
                    .replace(/<\/script/gi, '\\u003c/script');

                // Self-contained data injection script that:
                // 1. Calls myop_init_interface immediately if already defined
                // 2. Watches for late-defined myop_init_interface via defineProperty
                // 3. Cleans up after itself (removes script tag and window data)
                // 4. Posts message to parent for extension tracking (pre-injected init*)
                const dataScript = `<script id="__myop_init_data_script">
                    (function() {
                        var data = ${escapedData};
                        var scriptEl = document.getElementById('__myop_init_data_script');

                        var callInitInterface = function(fn) {
                            var startTime = performance.now();
                            // Mark as pre-injected data call for extension tracking
                            window.__myop_init_preinjected = true;
                            try {
                                fn(data);
                            } finally {
                                window.__myop_init_preinjected = false;
                            }
                            var duration = performance.now() - startTime;

                            // Notify extension via postMessage (init* = pre-injected)
                            try {
                                window.parent.postMessage({
                                    type: 'MYOP_PREINJECTED_INIT',
                                    payload: {
                                        data: data,
                                        duration: duration,
                                        timestamp: Date.now()
                                    }
                                }, '*');
                            } catch (e) {}

                            // Cleanup: remove script tag and stored data
                            if (scriptEl && scriptEl.parentNode) {
                                scriptEl.parentNode.removeChild(scriptEl);
                            }
                            delete window.__myop_init_data;
                        };

                        if (typeof window.myop_init_interface === 'function') {
                            // Function already exists - call immediately
                            callInitInterface(window.myop_init_interface);
                        } else {
                            // Function not yet defined - store data and watch for definition
                            window.__myop_init_data = data;

                            var _original;
                            Object.defineProperty(window, 'myop_init_interface', {
                                configurable: true,
                                enumerable: true,
                                set: function(fn) {
                                    _original = fn;
                                    if (window.__myop_init_data !== undefined) {
                                        var storedData = window.__myop_init_data;
                                        delete window.__myop_init_data;
                                        callInitInterface(fn);
                                    }
                                },
                                get: function() {
                                    return _original;
                                }
                            });
                        }
                    })();
                </script>`;
                doc.writeln(ctaQueueScript + dataScript);
            }

            doc.close();

            // TODO : control from dashboard
            //  (skin.loader as IIframeLoaderConfig).autoHeight = true;

            const comp = new MyopIframeComponent(componentDefinition, skin, container, IframeElement, options);

            // Store parsed sizeMeta on the component so the React host can read it
            comp.sizeMeta = parsedSizeMeta;

            // Auto-size: observe content size and sync to iframe dimensions.
            // This works for all hosts (vanilla SDK, React, Vue, Angular).
            // If a framework host (e.g. @myop/react) also calls observeAutoSize(),
            // it cleanly replaces these observers via _autoSizeCleanup tracking.
            comp.initiated().then(() => {
                if (options?.autoSize === false) {
                    // No auto-sizing. Iframe stays position:absolute filling container.
                    // Host is fully responsible for container dimensions (e.g. React Native WebView).
                    return;
                }
                let shown = false;
                comp.observeAutoSize({
                    forceAutoSize: true,
                    onSizeChange: () => {
                        // Show after first measurement completes (not before).
                        // The iframe transitions from absolute overlay to document flow
                        // at the measured height, preventing flash at wrong size.
                        if (!shown && !options?.hidden) {
                            shown = true;
                            comp.show();
                        }
                    }
                });
            });
            // // @ts-ignore
            // comp.props._global = comp.element.contentWindow;
            return comp;

        }

        if (loaderConfig.shadowRootMode === ShadowRootModeType.none) {
            // parser = parser ? parser : new DOMParser();
            // const doc = parser.parseFromString(loaderConfig.HTML, 'text/html');
            // element = doc.body.firstChild;
            //element.setAttribute('id', domId);

            const template = document.createElement('template');
            template.innerHTML = loaderConfig.HTML;//.trim();
            element = template.content.firstElementChild;

            //   const firstElement = template.content.firstElementChild;
            //   const z = firstElement ? firstElement.tagName.toLowerCase() : null;
            //   debugger;
            //   console.log(template, firstElement, z)
            //   element = firstElement

            // const container = document.createElement('tbody');
            // debugger;
            // container.innerHTML = loaderConfig.HTML;
            // element = container.firstElementChild;

            getInitiatedSdk().then((sdk) => {
                sdk.connectedCallback(element, element);
            })
        } else {
            const customElementName = toCustomElementName(componentDefinition.name + skin.id);

            //TODO : support redefine !
            if (!seen.includes(customElementName)) {
                createCustomWebComponent(customElementName, loaderConfig.HTML, loaderConfig.shadowRootMode);
                seen.push(customElementName);
            }
            element = document.createElement(customElementName);
        }
        // TODO : do it only with flag ?
        if (!container.querySelector('[id^="myop-comp-"]'))
            container.innerHTML = '';

        //element.style.visibility = 'hidden';
        element.id = domId;

        if (options?.hidden) {
            element.style.opacity = '0';
            element.style.position = 'absolute'
            element.style.height = '0'
            element.style.width = '0'
            element.style.pointerEvents = 'none'
            element.style.visibility = 'hidden';
        }

        // if (container.id.startsWith('myop-comp-')) {
        //     // in place replacement
        //     container.replaceWith(element);
        //     //container = element.parentElement!;
        // } else {
        //     container.appendChild(element);
        // }

        this.appendChild(container, element, options);

        //element.innerHTML = `<${customElementName} />`;
        return new MyopWebComponent(componentDefinition, skin, container, element, options);
    }
}
